import { Setting4 } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ESocialCheckStatus } from '@/store/api/Enterprise/Enterprise.types'
import { selectTeamsbyWorkspaceId } from '@/store/api/Users/Users.selector'
import { useGetWorkspacesQuery } from '@/store/api/Workspace/Workspace.query'
import { EReportFilter } from '@/store/api/Workspace/Workspace.types'
import type { RootState } from '@/store/store'

import type { ExtendedReportFilter } from '../Reports'

import type { ReportStatusType } from './ReportStatusSelector'

interface ReportsFilterSheetProps {
  selectedOfficeAndTeam: { workspace: string | null; team: string | null }
  selectedFilter?: ExtendedReportFilter | 'all'
  selectedStatus?: ReportStatusType
  onApplyFilters: (filters: {
    workspace: string | null
    team: string | null
    reportType?: ExtendedReportFilter | 'all'
    status?: ReportStatusType
  }) => void
  showReportTypeFilter?: boolean
  showStatusFilter?: boolean
  showAllOfficesOption?: boolean
}

export const ReportsFilterSheet = ({
  selectedOfficeAndTeam,
  selectedFilter = 'all',
  selectedStatus = 'all',
  onApplyFilters,
  showReportTypeFilter = true,
  showStatusFilter = true,
  showAllOfficesOption = true,
}: ReportsFilterSheetProps) => {
  const { t } = useTranslation()
  const { data: workspaceData } = useGetWorkspacesQuery()
  const [open, setOpen] = useState(false)

  const [localWorkspace, setLocalWorkspace] = useState<string | null>(
    selectedOfficeAndTeam.workspace,
  )
  const [localTeam, setLocalTeam] = useState<string | null>(
    selectedOfficeAndTeam.team,
  )
  const [localFilter, setLocalFilter] = useState<ExtendedReportFilter | 'all'>(
    selectedFilter,
  )
  const [localStatus, setLocalStatus] =
    useState<ReportStatusType>(selectedStatus)

  const teams = useSelector((state: RootState) =>
    selectTeamsbyWorkspaceId(state, localWorkspace),
  )

  useEffect(() => {
    if (open) {
      setLocalWorkspace(selectedOfficeAndTeam.workspace)
      setLocalTeam(selectedOfficeAndTeam.team)
      setLocalFilter(selectedFilter)
      setLocalStatus(selectedStatus)
    }
  }, [open])

  const handleWorkspaceChange = (workspaceId: string | null) => {
    if (workspaceId === localWorkspace) {
      if (!showAllOfficesOption && workspaceId !== null) return
      setLocalWorkspace(null)
      setLocalTeam(null)
    } else {
      setLocalWorkspace(workspaceId)
      if (!showAllOfficesOption && workspaceId) {
        const ws = workspaces.find((w) => w._id === workspaceId)
        setLocalTeam(ws?.teams?.[0]?._id ?? null)
      } else {
        setLocalTeam(null)
      }
    }
  }

  const handleTeamChange = (teamId: string | null) => {
    if (teamId === localTeam) {
      if (!showAllOfficesOption && teamId !== null) return
      setLocalTeam(null)
    } else {
      setLocalTeam(teamId)
    }
  }

  const handleFilterChange = (filter: ExtendedReportFilter | 'all') => {
    setLocalFilter(filter)
  }

  const handleStatusChange = (status: ReportStatusType) => {
    setLocalStatus(status)
  }

  const handleSave = () => {
    onApplyFilters({
      workspace: localWorkspace,
      team: localTeam,
      ...(showReportTypeFilter && { reportType: localFilter }),
      ...(showStatusFilter && { status: localStatus }),
    })
    setOpen(false)
  }

  const workspaces = workspaceData?.info?.workspaces ?? []

  const reportTypeOptions: {
    value: ExtendedReportFilter | 'all'
    label: string
  }[] = [
      { value: 'all', label: t('dashboard.reportsScreen.all') },
      {
        value: 'your_reports',
        label: t('dashboard.reportsScreen.yourReports'),
      },
      {
        value: EReportFilter.Ai,
        label: t('dashboard.reportsScreen.aIReport'),
      },
      {
        value: EReportFilter.AiAnalyst,
        label: t('dashboard.reportsScreen.aIAnalystReport'),
      },
      {
        value: EReportFilter.AdverseMedia,
        label: t('dashboard.reportsScreen.adverseMedia'),
      },
      {
        value: EReportFilter.Assited,
        label: t('dashboard.reportsScreen.weCheckAssisted'),
      },
      {
        value: EReportFilter.FacialMatching,
        label: t('dashboard.reportsScreen.facialMatchingCredit'),
      },
    ]

  const statusOptions: { value: ReportStatusType; label: string }[] = [
    { value: 'all', label: t('dashboard.reportsScreen.all') },
    {
      value: ESocialCheckStatus.InProgress,
      label: t('dashboard.reportsScreen.inProgress'),
    },
    { value: 'new', label: t('dashboard.reportsScreen.new') },
    {
      value: ESocialCheckStatus.NotReleased,
      label: t('dashboard.reportsScreen.concentPending'),
    },
    {
      value: ESocialCheckStatus.Cancelled,
      label: t('dashboard.reportsScreen.cancelled'),
    },
  ]

  const FilterRow = ({
    checked,
    onToggle,
    label,
    indented,
  }: {
    checked: boolean
    onToggle: () => void
    label: string
    indented?: boolean
  }) => (
    <label
      className={`flex items-center gap-3 cursor-pointer rounded-lg border border-neutralColor5 px-3 py-2.5 bg-baseColorHighlight transition-colors hover:bg-baseColor ${indented ? 'ml-7' : ''
        }`}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span className="text-sm text-neutralColor">{label}</span>
    </label>
  )

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="secondary" leftIcon={<Setting4 size={16} />}>
          {t('dashboard.reportsScreen.filters.title')}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex flex-col gap-0">
        <SheetHeader className="pb-2">
          <SheetTitle className="text-lg font-medium text-neutralColor">
            {t('dashboard.reportsScreen.filters.sheetTitle')}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <Accordion type="multiple" defaultValue={[]}>
            {/* Team Section */}
            <AccordionItem value="team" className="border-b-0">
              <AccordionTrigger className="hover:no-underline text-base font-medium text-neutralColor py-3">
                {t('dashboard.reportsScreen.filters.team')}
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2">
                  {showAllOfficesOption && (
                    <FilterRow
                      checked={localWorkspace === null}
                      onToggle={() => handleWorkspaceChange(null)}
                      label={t('dashboard.reportsScreen.filters.allOffices')}
                    />
                  )}
                  {workspaces.map((workspace) => (
                    <div key={workspace._id} className="flex flex-col gap-2">
                      <FilterRow
                        checked={localWorkspace === workspace._id}
                        onToggle={() => handleWorkspaceChange(workspace._id)}
                        label={workspace.name}
                      />
                      {localWorkspace === workspace._id && teams && (
                        <div className="flex flex-col gap-2">
                          {showAllOfficesOption && (
                            <FilterRow
                              checked={localTeam === null}
                              onToggle={() => handleTeamChange(null)}
                              label={t(
                                'dashboard.reportsScreen.filters.allTeams',
                              )}
                              indented
                            />
                          )}
                          {teams.map((team) => (
                            <FilterRow
                              key={team._id}
                              checked={localTeam === team._id}
                              onToggle={() => handleTeamChange(team._id)}
                              label={team.name}
                              indented
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Report Type Section */}
            {showReportTypeFilter && (
              <AccordionItem value="reportType" className="border-b-0">
                <AccordionTrigger className="hover:no-underline text-base font-medium text-neutralColor py-3">
                  {t('dashboard.reportsScreen.filters.reportType')}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2">
                    {reportTypeOptions.map((option) => (
                      <FilterRow
                        key={option.value ?? 'all'}
                        checked={localFilter === option.value}
                        onToggle={() => handleFilterChange(option.value)}
                        label={option.label}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Status Section */}
            {showStatusFilter && (
              <AccordionItem value="status" className="border-b-0">
                <AccordionTrigger className="hover:no-underline text-base font-medium text-neutralColor py-3">
                  {t('dashboard.reportsScreen.filters.status')}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2">
                    {statusOptions.map((option) => (
                      <FilterRow
                        key={option.value}
                        checked={localStatus === option.value}
                        onToggle={() => handleStatusChange(option.value)}
                        label={option.label}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>

        <SheetFooter className="pt-4 sm:justify-end">
          <Button onClick={handleSave} className="rounded-full px-6">
            {t('dashboard.reportsScreen.filters.save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
